import { useEffect, useRef, useState } from "react";
import { getDireccionesByCliente } from "../../../services/usuarios_tienda/clientes_direccion";

function CustomerAddressesModal({ isOpen, onClose, cliente }) {
  const [direcciones, setDirecciones] = useState([]);
  const [loading, setLoading] = useState(false);

  const [alert, setAlert] = useState(null);
  const alertTimerRef = useRef(null);

  const showAlert = (type, message) => {
    setAlert({ type, message: String(message || "") });
    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 4000);
  };

  const cargarDirecciones = async (clienteId) => {
    setLoading(true);
    setAlert(null);

    try {
      const data = await getDireccionesByCliente(clienteId);
      setDirecciones(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error cargando direcciones:", error);
      showAlert("error", error?.message || "No se pudieron cargar las direcciones.");
      setDirecciones([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    if (!cliente?.cliente_id) {
      setDirecciones([]);
      return;
    }

    cargarDirecciones(cliente.cliente_id);

    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, cliente?.cliente_id]);

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const fullName = `${cliente?.nombre || ""} ${cliente?.apellido || ""}`.trim();

  const buildAddress = (d) => {
    const parts = [
      d?.direccion_linea_1,
      d?.direccion_linea_2,
      d?.ciudad,
      d?.estado_provincia,
      d?.codigo_postal,
      d?.pais,
    ].filter(Boolean);
    return parts.join(", ");
  };

  return (
    <div
      style={ui.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose?.();
      }}
    >
      <div style={ui.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div style={ui.header}>
          <h2 style={ui.title}>
            Direcciones de {fullName || "Cliente"}
          </h2>

          <button
            style={ui.closeBtn}
            onClick={onClose}
            disabled={loading}
            title="Cerrar"
          >
            ✕
          </button>
        </div>

        {alert && (
          <div
            style={{
              ...ui.alert,
              ...(alert.type === "error" ? ui.alertError : ui.alertInfo),
            }}
          >
            <span>{alert.message}</span>
            <button
              style={ui.alertX}
              onClick={() => setAlert(null)}
              disabled={loading}
              title="Cerrar alerta"
            >
              ✕
            </button>
          </div>
        )}

        <div style={ui.tableWrapper}>
          {loading ? (
            <div style={ui.loadingBox}>Cargando direcciones...</div>
          ) : (
            <table style={ui.table}>
              <colgroup>
                <col style={{ width: 80 }} />
                <col style={{ width: 180 }} />
                <col style={{ width: 420 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 160 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 110 }} />
              </colgroup>

              <thead style={ui.thead}>
                <tr>
                  <th style={ui.th}>ID</th>
                  <th style={ui.th}>Nombre</th>
                  <th style={ui.th}>Dirección</th>
                  <th style={ui.th}>País</th>
                  <th style={ui.th}>Departamento / Estado</th>
                  <th style={ui.th}>Código postal</th>
                  <th style={ui.th}>Principal</th>
                </tr>
              </thead>

              <tbody>
                {direcciones.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={ui.noData}>
                      No se encontraron direcciones
                    </td>
                  </tr>
                ) : (
                  direcciones.map((d) => {
                    const fullAddress = buildAddress(d);

                    return (
                      <tr key={d.cliente_direccion_id} style={ui.row}>
                        <td style={ui.td}>{d.cliente_direccion_id}</td>

                        <td style={ui.td}>
                          <div style={ui.cellWrap} title={d?.nombre_destinatario || ""}>
                            {d?.nombre_destinatario || "-"}
                          </div>
                        </td>

                        <td style={{ ...ui.td, ...ui.tdLong }}>
                          <div style={ui.cellWrap} title={fullAddress}>
                            {fullAddress || "-"}
                          </div>
                        </td>

                        <td style={ui.td}>
                          <div style={ui.cellWrap} title={d?.pais || ""}>
                            {d?.pais || "-"}
                          </div>
                        </td>

                        <td style={ui.td}>
                          <div style={ui.cellWrap} title={d?.estado_provincia || ""}>
                            {d?.estado_provincia || "-"}
                          </div>
                        </td>

                        <td style={ui.td}>
                          <div style={ui.cellWrap} title={d?.codigo_postal || ""}>
                            {d?.codigo_postal || "-"}
                          </div>
                        </td>

                        <td style={ui.td}>
                          <span style={Number(d.direccion_principal) ? ui.tagActive : ui.tagInactive}>
                            {Number(d.direccion_principal) ? "Sí" : "No"}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>

        <div style={ui.footer}>
          <button style={ui.closeButton} onClick={onClose} disabled={loading}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

const ui = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    zIndex: 2000,
  },

  modal: {
    width: "min(1100px, 96vw)",
    background: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 6px 22px rgba(0,0,0,0.15)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    maxHeight: "85vh",
  },

  header: {
    padding: "16px 24px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
  },

  title: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#1f2937",
    margin: 0,
  },

  closeBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "22px",
    color: "#6b7280",
    opacity: 0.9,
  },

  alert: {
    margin: "14px 22px 0",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    fontSize: "13px",
    fontWeight: 600,
  },
  alertError: {
    background: "#FEF2F2",
    borderColor: "#FECACA",
    color: "#7F1D1D",
  },
  alertInfo: {
    background: "#EFF6FF",
    borderColor: "#BFDBFE",
    color: "#1E3A8A",
  },
  alertX: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "16px",
    opacity: 0.7,
  },

  tableWrapper: {
    flexGrow: 1,
    overflowY: "auto",
    overflowX: "auto",
    padding: "22px",
  },

  loadingBox: {
    padding: "16px",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    color: "#475569",
    fontSize: "14px",
    fontWeight: 600,
  },

  table: {
    width: "100%",
    minWidth: 1000,
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  thead: {
    position: "sticky",
    top: 0,
    background: "#F1F5F9",
    zIndex: 5,
  },

  th: {
    textAlign: "left",
    padding: "12px",
    fontSize: "13px",
    color: "#475569",
    borderBottom: "1px solid #E2E8F0",
    whiteSpace: "nowrap",
  },

  td: {
    padding: "12px",
    fontSize: "14px",
    color: "#334155",
    borderBottom: "1px solid #F1F5F9",
    verticalAlign: "top",
  },

  tdLong: {
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    lineHeight: 1.35,
  },

  cellWrap: {
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },

  row: {
    borderBottom: "1px solid #E2E8F0",
  },

  noData: {
    padding: "16px",
    textAlign: "center",
    color: "#64748b",
    fontSize: "14px",
  },

  footer: {
    padding: "16px 24px",
    borderTop: "1px solid #e5e7eb",
    textAlign: "right",
  },

  closeButton: {
    padding: "10px 18px",
    background: "#e5e7eb",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    color: "#1f2937",
    fontWeight: 500,
  },

  tagActive: {
    padding: "6px 10px",
    borderRadius: "20px",
    background: "#DCFCE7",
    color: "#15803D",
    fontSize: "12px",
    fontWeight: 600,
    display: "inline-block",
    whiteSpace: "nowrap",
  },

  tagInactive: {
    padding: "6px 10px",
    borderRadius: "20px",
    background: "#FEE2E2",
    color: "#B91C1C",
    fontSize: "12px",
    fontWeight: 600,
    display: "inline-block",
    whiteSpace: "nowrap",
  },
};

export default CustomerAddressesModal;
